import type {
  SearchObjectFieldsTypeFromMappingObjectType,
  SearchResult,
} from './types'
import type { SearchResponse } from './types'
import { usersMapping } from '../indexMappings/users'
import { buildUrl } from '../utils/build-url'
import { fetchThrowsIfNotOk } from '../utils/fetch-throws-if-not-ok'

export function strToDate(date: string | Date): Date {
  if (typeof date === 'string') {
    return new Date(date)
  }
  return date
}

type SearchUsersFields = SearchObjectFieldsTypeFromMappingObjectType<
  typeof usersMapping
>

export type SearchUsersRequiredFields = {
  [K in keyof Pick<
    SearchUsersFields,
    | 'displayName'
    | 'username'
    | 'email'
    | 'userId'
    | 'photoURL'
    | 'createdAt'
    | 'updatedAt'
    | 'isArchived'
    | 'invitePending'
    | 'organizationRole'
  >]: SearchUsersFields[K]
}

// Only used to have type safety and SEARCH_NOTE_REQUIRED_FIELDS matching SearchRequiredFields type definition
const searchUsersRequiredFieldsHelper: {
  [K in keyof SearchUsersRequiredFields]: true
} = {
  createdAt: true,
  displayName: true,
  email: true,
  invitePending: true,
  isArchived: true,
  organizationRole: true,
  photoURL: true,
  updatedAt: true,
  userId: true,
  username: true,
}

export const SEARCH_USERS_REQUIRED_FIELDS = Object.freeze(
  Object.keys(searchUsersRequiredFieldsHelper)
)

export type SearchUsersResult = SearchResult<SearchUsersRequiredFields>

export type SearchUsersResponse = SearchResponse<SearchUsersResult>

export enum UserSearchableField {
  Email = 'email',
  DisplayName = 'displayName',
}

export type SearchUsersOptionalFilters = {
  isArchived?: boolean
  isGuest?: boolean
  isReader?: boolean
  invitePending?: boolean
  excludeUserIdList?: string[]
  restrictSearchableFieldList?: UserSearchableField[]
}

export type SearchUsersQuery = {
  query: string
  optionalFilters?: SearchUsersOptionalFilters
  hitsPerPage: number
  page: number
  explain?: boolean
  profile?: boolean
}

export type SearchUsersHit = {
  id: string
  displayName: string
  photoURL: string | null
  email: string
  createdAt: Date
  organizationRole: string
  updatedAt: Date
  isArchived: boolean
  invitePending: boolean
}

export async function fetchUsers({
  baseUrl,
  token,
  query,
  hitsPerPage,
  optionalFilters,
  page,
}: {
  baseUrl: string
  token: string
} & SearchUsersQuery): Promise<
  Pick<SearchUsersResponse, 'nbPages' | 'page'> & { hits: SearchUsersHit[] }
> {
  const response = await fetchThrowsIfNotOk(buildUrl(baseUrl, '/users'), {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    method: 'post',
    body: JSON.stringify({
      optionalFilters,
      query,
      hitsPerPage,
      page,
    }),
  })

  const result: SearchUsersResponse = await response.json()

  const hits: SearchUsersHit[] = result.hits.map((hit) => ({
    id: hit.fields?.userId?.[0] ?? '',
    displayName: hit.fields?.displayName?.[0] ?? '',
    photoURL: hit.fields?.photoURL?.[0] ?? null,
    email: hit.fields?.email?.[0] ?? '',
    createdAt: strToDate(hit.fields?.createdAt?.[0] ?? ''),
    organizationRole: hit.fields?.organizationRole?.[0] ?? '',
    updatedAt: strToDate(hit.fields?.updatedAt?.[0] ?? ''),
    isArchived: hit.fields?.isArchived?.[0] ?? false,
    invitePending: hit.fields?.invitePending?.[0] ?? false,
  }))

  return {
    nbPages: result.nbPages,
    page: result.page,
    hits,
  }
}
